/**
 * The ledger's cell cursor: where the selection is and where each key sends it.
 *
 * No React in here, because none of it needs any. Movement over a grid with merged cells is the
 * part of a spreadsheet that is easy to get subtly wrong -- a heading that spans the block, an
 * arrow that loses which column you started in -- and it is far cheaper to pin down against plain
 * data than against rendered markup.
 *
 * A *pane* is one table. The ledger draws two side by side, the way her sheet has the budget in
 * A:D and the recordings in E:L, and moving off the right edge of one continues into the other
 * rather than stopping. Vertical movement stays inside a pane: the two have different row counts
 * and nothing lines up across them, so carrying a row index over would land somewhere arbitrary.
 *
 *   pane = { key, rows: [ { key, section, cells: [ { key, span, editable } ] } ] }
 *
 * The cursor is `{ pane, row, column }`, and `column` is a *column* rather than a cell index. That
 * is what makes vertical movement through a spanning cell behave: passing down through a group
 * heading and out the other side puts you back in the column you left, because the column never
 * changed -- only which cell happens to cover it.
 */

function span(cell) {
	return cell.span || 1;
}

/** How many columns wide a row is, spans counted. */
export function rowWidth(row) {
	return row.cells.reduce((total, cell) => total + span(cell), 0);
}

/** The index of the cell covering `column`, clamped to the row rather than returning nothing. */
export function cellIndexAt(row, column) {
	let end = 0;
	for(let index = 0; index < row.cells.length; index++) {
		end += span(row.cells[index]);
		if(column < end) {
			return index;
		}
	}
	return row.cells.length - 1;
}

/** The first column a cell covers -- where the cursor lands when it is selected sideways. */
export function columnOfCell(row, cellIndex) {
	let column = 0;
	for(let index = 0; index < cellIndex && index < row.cells.length; index++) {
		column += span(row.cells[index]);
	}
	return column;
}

function clamp(value, low, high) {
	return Math.min(Math.max(value, low), high);
}

function paneHasRows(pane) {
	return !!pane && !!pane.rows && pane.rows.length > 0;
}

/** The first cell of the first pane that has any rows, or null when there is nothing to select. */
export function firstCursor(panes) {
	for(let index = 0; index < panes.length; index++) {
		if(paneHasRows(panes[index])) {
			return { pane: index, row: 0, column: 0 };
		}
	}
	return null;
}

/**
 * What a cursor actually points at.
 *
 * Rows and columns are clamped rather than validated, so a cursor that was sitting on the last row
 * of a month survives that month losing a transaction instead of leaving nothing selected.
 *
 * @returns {{paneIndex, pane, rowIndex, row, cellIndex, cell, column}|null}
 */
export function resolveCursor(panes, cursor) {
	if(!cursor) {
		return null;
	}
	const pane = panes[cursor.pane];
	if(!paneHasRows(pane)) {
		return null;
	}
	const rowIndex = clamp(cursor.row, 0, pane.rows.length - 1);
	const row = pane.rows[rowIndex];
	if(!row.cells.length) {
		return null;
	}
	const cellIndex = cellIndexAt(row, cursor.column);
	return {
		paneIndex: cursor.pane,
		pane,
		rowIndex,
		row,
		cellIndex,
		cell: row.cells[cellIndex],
		column: cursor.column,
	};
}

/** The cursor that selects a particular cell -- what a click on one resolves to. */
export function cursorForCell(panes, paneIndex, rowIndex, cellIndex) {
	const pane = panes[paneIndex];
	if(!paneHasRows(pane) || !pane.rows[rowIndex]) {
		return null;
	}
	const row = pane.rows[rowIndex];
	return { pane: paneIndex, row: rowIndex, column: columnOfCell(row, clamp(cellIndex, 0, row.cells.length - 1)) };
}

function neighbourPane(panes, from, step) {
	for(let index = from + step; index >= 0 && index < panes.length; index += step) {
		if(paneHasRows(panes[index])) {
			return index;
		}
	}
	return null;
}

function intoPane(panes, paneIndex, rowIndex, edge) {
	const pane = panes[paneIndex];
	const row = pane.rows[clamp(rowIndex, 0, pane.rows.length - 1)];
	const cellIndex = edge === 'end' ? row.cells.length - 1 : 0;
	return { pane: paneIndex, row: clamp(rowIndex, 0, pane.rows.length - 1), column: columnOfCell(row, cellIndex) };
}

/**
 * The cursor a key produces.
 *
 * Always returns a cursor: a move that has nowhere to go returns the one it was given, so a held
 * arrow key at the edge of the grid does nothing rather than wrapping around to the far side.
 *
 * @param panes {Array} the panes as drawn
 * @param cursor {Object} where the selection is now
 * @param direction {string} up/down/left/right/rowStart/rowEnd/gridStart/gridEnd/pageUp/pageDown
 * @param pageRows {number} how far a page key travels
 */
export default function moveCursor(panes, cursor, direction, pageRows = 10) {
	const at = resolveCursor(panes, cursor);
	if(!at) {
		return firstCursor(panes);
	}
	const { paneIndex, pane, rowIndex, row, cellIndex } = at;

	switch(direction) {
		case 'right': {
			if(cellIndex < row.cells.length - 1) {
				return { pane: paneIndex, row: rowIndex, column: columnOfCell(row, cellIndex + 1) };
			}
			const next = neighbourPane(panes, paneIndex, 1);
			return next === null ? cursor : intoPane(panes, next, rowIndex, 'start');
		}
		case 'left': {
			if(cellIndex > 0) {
				return { pane: paneIndex, row: rowIndex, column: columnOfCell(row, cellIndex - 1) };
			}
			const previous = neighbourPane(panes, paneIndex, -1);
			return previous === null ? cursor : intoPane(panes, previous, rowIndex, 'end');
		}
		case 'up':
			return rowIndex === 0 ? cursor : { ...cursor, row: rowIndex - 1 };
		case 'down':
			return rowIndex === pane.rows.length - 1 ? cursor : { ...cursor, row: rowIndex + 1 };
		case 'pageUp':
			return { ...cursor, row: clamp(rowIndex - pageRows, 0, pane.rows.length - 1) };
		case 'pageDown':
			return { ...cursor, row: clamp(rowIndex + pageRows, 0, pane.rows.length - 1) };
		case 'rowStart':
			return { pane: paneIndex, row: rowIndex, column: 0 };
		case 'rowEnd':
			return { pane: paneIndex, row: rowIndex, column: columnOfCell(row, row.cells.length - 1) };
		case 'gridStart':
			return firstCursor(panes) || cursor;
		case 'gridEnd': {
			const last = neighbourPane(panes, panes.length, -1);
			return last === null ? cursor : intoPane(panes, last, panes[last].rows.length - 1, 'end');
		}
		default:
			return cursor;
	}
}
